import { Link } from 'react-router-dom'

export function MissionPage() {
  return (
    <div className="min-h-[100dvh] flex flex-col bg-gradient-to-br from-stone-900 via-stone-900 to-amber-950">
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-orange-500/8 rounded-full blur-3xl" />
      </div>

      {/* Sticky nav */}
      <div className="sticky top-0 z-50 bg-stone-900/80 backdrop-blur border-b border-white/10">
        <div className="flex items-center justify-between px-4 py-3 max-w-lg mx-auto w-full">
          <Link to="/about-b" className="flex items-center gap-2 text-stone-400 hover:text-white transition-colors text-sm">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back to About
          </Link>
          <Link to="/auth?mode=signup" className="btn-primary text-sm px-4 py-2 font-semibold">
            Sign Up Free
          </Link>
        </div>
      </div>

      <div className="relative flex flex-col flex-1 max-w-lg mx-auto w-full px-6">

        {/* Header */}
        <div className="pt-14 pb-10">
          <p className="text-amber-400 text-sm font-semibold uppercase tracking-wider mb-3">Our story</p>
          <h1 className="text-3xl font-bold text-white leading-tight">
            Why we built The Beacon
          </h1>
        </div>

        {/* You already know someone */}
        <section className="py-10 border-t border-white/10">
          <h2 className="text-2xl font-bold text-white mb-5 leading-snug">
            You already know someone who needed this.
          </h2>
          <div className="space-y-4 text-stone-300 text-base leading-relaxed">
            <p>You just didn't know it at the time.</p>
            <p>
              Most of us have been there. A friend goes quiet. Life gets busy. Weeks pass. And then one day you get a call that changes everything — and you're left wondering how you didn't see it coming. Wondering what would have happened if you had just reached out.
            </p>
            <p>
              You're not alone in that feeling. Nearly everyone has lost someone to suicide, or knows someone who has. It is one of the most common, most silent, and most preventable tragedies of our time.
            </p>
            <p className="text-amber-300 font-medium">
              We built The Beacon because we've been there too.
            </p>
          </div>
        </section>

        {/* The scale */}
        <section className="py-10 border-t border-white/10">
          <h2 className="text-2xl font-bold text-white mb-6">The scale of it</h2>
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-6 text-center mb-6">
            <p className="text-5xl font-bold text-amber-400 mb-1">47,000</p>
            <p className="text-stone-300 text-sm">people die by suicide in the US every year</p>
            <p className="text-stone-500 text-xs mt-2">For every death, an estimated 25 attempts.</p>
          </div>
          <p className="text-stone-300 text-base leading-relaxed">
            Behind almost every one of those is a person who felt unseen — and a circle of people who wish they had reached out sooner.
          </p>
        </section>

        {/* What the research shows */}
        <section className="py-10 border-t border-white/10">
          <h2 className="text-2xl font-bold text-white mb-5">What the research shows</h2>
          <div className="space-y-4 text-stone-300 text-base leading-relaxed">
            <p>
              Social connection is one of the most powerful protective factors against suicide. Not professional intervention alone. Not crisis hotlines alone.
            </p>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
              <p className="text-white font-semibold text-lg leading-snug">
                Everyday human connection.
              </p>
              <p className="text-stone-400 text-sm mt-2 leading-relaxed">
                The kind that says <em>you matter to me</em> before things ever reach a breaking point.
              </p>
            </div>
            <p>
              The Beacon exists in that space — the quiet, everyday layer of connection that fills the gap before someone reaches a crisis point. It doesn't replace therapy or crisis support. It fills the gap that exists before someone needs them.
            </p>
          </div>
        </section>

        {/* The name */}
        <section className="py-10 border-t border-white/10">
          <h2 className="text-2xl font-bold text-white mb-5">The name means something to us</h2>
          <div className="space-y-4 text-stone-300 text-base leading-relaxed">
            <p>
              A beacon doesn't demand your attention. It doesn't compete for it. It simply shines — steady and quiet — so that the people watching for it know where to look.
            </p>
            <p>
              That's what we want The Beacon to be in your life. Not another app pulling you in. A quiet presence in the background that keeps you tethered to the people who matter — across every chapter, every distance, every season of life.
            </p>
            <p className="text-amber-300 font-medium">
              Real connection doesn't have to be loud. Sometimes it's just a green light that says <em>I'm okay</em> — and knowing that someone who loves you saw it.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-10 border-t border-white/10 space-y-4">
          <h2 className="text-2xl font-bold text-white leading-snug">Be the reason someone feels seen.</h2>
          <p className="text-stone-300 text-base leading-relaxed">
            A beacon only helps if someone is watching for the light. When you sign up, bring your circle with you. You don't need to make it a moment. Just say:{' '}
            <span className="text-white font-medium italic">"I joined something. I want you in my circle."</span>
          </p>
          <p className="text-amber-300 font-medium">That message might matter more than you know.</p>

          <Link to="/auth?mode=signup" className="block w-full text-center btn-primary text-base font-bold py-4 shadow-lg shadow-amber-500/30 mt-2">
            Sign Up Free
          </Link>
        </section>

        {/* 988 footer */}
        <section className="py-8 border-t border-white/10">
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-5 text-center space-y-2">
            <p className="text-stone-300 text-sm leading-relaxed">
              The Beacon is dedicated to suicide prevention through everyday human connection.
            </p>
            <p className="text-stone-300 text-sm leading-relaxed">
              If you or someone you know is in crisis, please{' '}
              <a href="tel:988" className="text-amber-400 font-semibold underline underline-offset-2">call or text 988</a>{' '}
              (Suicide &amp; Crisis Lifeline).
            </p>
          </div>
        </section>

        <div className="pb-10 text-center">
          <Link to="/" className="text-stone-600 hover:text-stone-400 text-xs transition-colors">- jointhebeacon.com</Link>
        </div>

      </div>
    </div>
  )
}

export default MissionPage
